import { useEffect, useState } from "react";
import api from "@/lib/api/client";

const CACHED_PREFIXES = ["/api/knowledge/threads/", "/api/library/creatives/"];
const MAX_CACHE_BYTES = 64 * 1024 * 1024;
const MAX_CACHE_ENTRIES = 60;

interface CacheEntry {
  url: string;
  refs: number;
  bytes: number;
}

/**
 * In-tab cache for thread and Library media. Their paths carry immutable tokens
 * (no signature or expiry), so the path is a stable identity. A Library token
 * maps to one file for life: the update route refuses new source bytes and there
 * is no replace route, so a new file is a new token. The server keeps
 * `no-store`; this only avoids refetching within one tab. Map insertion order
 * is the LRU order. Entries with refs > 0 are never revoked.
 */
const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<CacheEntry>>();

function getCacheKey(source: string): string | null {
  if (!CACHED_PREFIXES.some((prefix) => source.startsWith(prefix))) return null;
  return source.split(/[?#]/)[0];
}

function evictUnused() {
  let totalBytes = 0;
  cache.forEach((entry) => {
    totalBytes += entry.bytes;
  });
  for (const [key, entry] of Array.from(cache.entries())) {
    if (totalBytes <= MAX_CACHE_BYTES && cache.size <= MAX_CACHE_ENTRIES) {
      return;
    }
    if (entry.refs > 0) continue;
    URL.revokeObjectURL(entry.url);
    cache.delete(key);
    totalBytes -= entry.bytes;
  }
}

/**
 * Deferred so a remount that reads the cached URL during render and acquires it
 * in its effect (same commit as the old instance's cleanup) is counted before
 * anything is revoked. Also runs after every waiter of a shared fetch settles.
 */
function scheduleEvictUnused() {
  queueMicrotask(evictUnused);
}

function loadCached(key: string, source: string): Promise<CacheEntry> {
  const existing = cache.get(key);
  if (existing) return Promise.resolve(existing);
  const pending = inflight.get(key);
  if (pending) return pending;
  const request = api
    .get<Blob>(source.slice(4), { responseType: "blob" })
    .then(({ data }) => {
      const entry: CacheEntry = {
        url: URL.createObjectURL(data),
        refs: 0,
        bytes: data.size ?? 0,
      };
      cache.set(key, entry);
      return entry;
    })
    .finally(() => {
      inflight.delete(key);
    });
  inflight.set(key, request);
  return request;
}

function readCachedUrl(source?: string | null): string | null {
  const key = source ? getCacheKey(source) : null;
  return (key && cache.get(key)?.url) || null;
}

/** True when this image would show at once, with no request. */
export function hasCachedPrivateMedia(source?: string | null): boolean {
  return Boolean(readCachedUrl(source));
}

/** Drops every cached entry and revokes its object URL. Runs on logout. */
export function clearPrivateMediaCache() {
  cache.forEach((entry) => URL.revokeObjectURL(entry.url));
  cache.clear();
  inflight.clear();
}

/** Private app media uses the authenticated client; credentials never enter image URLs. */
export function usePrivateMedia(source?: string | null) {
  const [state, setState] = useState<{
    source?: string | null;
    src: string | null;
    error: boolean;
  }>(() => ({ source, src: readCachedUrl(source), error: false }));
  useEffect(() => {
    if (!source?.startsWith("/api/")) return;
    const key = getCacheKey(source);
    if (key) {
      let active = true;
      let held: CacheEntry | null = null;
      const acquire = (entry: CacheEntry) => {
        entry.refs += 1;
        held = entry;
        // Refresh LRU position.
        cache.delete(key);
        cache.set(key, entry);
        setState({ source, src: entry.url, error: false });
        evictUnused();
      };
      const hit = cache.get(key);
      if (hit) {
        acquire(hit);
      } else {
        setState({ source, src: null, error: false });
      }
      void (hit ? Promise.resolve(hit) : loadCached(key, source))
        .then((entry) => {
          if (held) return;
          if (!active) {
            // Stored after its consumer left: still subject to the caps.
            scheduleEvictUnused();
            return;
          }
          acquire(entry);
        })
        .catch(() => {
          if (active) setState({ source, src: null, error: true });
        });
      return () => {
        active = false;
        if (held) {
          held.refs -= 1;
          scheduleEvictUnused();
        }
      };
    }
    let objectUrl: string | null = null;
    let active = true;
    const controller = new AbortController();
    setState({ source, src: null, error: false });
    void api
      .get<Blob>(source.slice(4), {
        responseType: "blob",
        signal: controller.signal,
      })
      .then(({ data }) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(data);
        setState({ source, src: objectUrl, error: false });
      })
      .catch(() => {
        if (active && !controller.signal.aborted)
          setState({ source, src: null, error: true });
      });
    return () => {
      active = false;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [source]);
  if (!source?.startsWith("/api/"))
    return { src: source ?? null, error: false };
  return state.source === source
    ? { src: state.src, error: state.error }
    : { src: readCachedUrl(source), error: false };
}
